import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getAllPayments,
  getPayments,
  FILE_BASE,
} from "../../../store/actions/councilActions/centerFeeActions";
import { exportToCSV } from "../../../utils/exportCsv";
import Paging from "../../Common/Paging";

/*
 * Admin: every payment the site has taken.
 *
 * Student and examination payments are stored separately from center
 * registration and renewal payments, so this pulls them together into one list
 * rather than leaving admin with two half-views. The totals across the top
 * always describe the whole filtered set, not just the page on screen.
 */
const rupees = (value) => "₹" + Number(value || 0).toLocaleString("en-IN");

const dmy = (value) =>
  value ? String(value).slice(0, 10).split("-").reverse().join("-") : "-";

const TYPES = [
  { value: "all", label: "All payments" },
  { value: "student", label: "Student registration" },
  { value: "exam", label: "Examination fee" },
  { value: "center", label: "Center registration" },
  { value: "renewal", label: "Center renewal" },
];

const Payments = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [filters, setFilters] = useState({
    type: "all",
    mode: "all",
    status: "all",
    search: "",
    from: "",
    to: "",
  });

  const load = useCallback(
    async (over = {}) => {
      setLoading(true);
      const result = await dispatch(
        getPayments({ ...filters, page: 1, per_page: 10, ...over })
      );
      setData(result || false);
      setLoading(false);
    },
    [dispatch, filters]
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const set = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    // Typing needs an explicit search; dropdowns and dates apply straight away.
    if (key !== "search") load({ ...next, page: 1 });
  };

  /* The export covers every matching payment, not just the visible page. */
  const download = async () => {
    setDownloading(true);
    const all = await dispatch(getAllPayments(filters));
    setDownloading(false);
    if (!all || !all.payments.length) return;

    exportToCSV(
      all.payments.map((p, i) => ({
        "Sr No": i + 1,
        Date: (p.paid_at || "").slice(0, 19),
        Type: p.type_label,
        "Paid By": p.paid_by,
        Reference: p.reference || "",
        Amount: p.amount,
        Mode: p.channel_label || p.mode,
        "Transaction ID": p.trx_id || "",
        Receipt: p.receipt_num || "",
        Status: p.status || "",
        Note: p.note || "",
      })),
      "nsvec-payments"
    );
  };

  const firstSr = data?.pagination?.first_sr || 1;

  return (
    <div>
      <div class="BigTitle">Payments</div>

      {data && (
        <div class="row mt-3">
          <div class="col-md-3">
            <div class="card p-3 mb-2">
              <small class="text-muted">Payments</small>
              <h4 class="mb-0">{data.totals.count}</h4>
            </div>
          </div>
          <div class="col-md-3">
            <div class="card p-3 mb-2">
              <small class="text-muted">Total</small>
              <h4 class="mb-0">{rupees(data.totals.amount)}</h4>
            </div>
          </div>
          <div class="col-md-3">
            <div class="card p-3 mb-2">
              <small class="text-muted">Through gateway</small>
              <h4 class="mb-0 text-success">{rupees(data.totals.online)}</h4>
            </div>
          </div>
          <div class="col-md-3">
            <div class="card p-3 mb-2">
              <small class="text-muted">Entered by hand</small>
              <h4 class="mb-0">{rupees(data.totals.offline)}</h4>
            </div>
          </div>
        </div>
      )}

      {data && data.totals.unverified > 0 && (
        <div class="alert alert-warning">
          {data.totals.unverified} payment(s) could not be confirmed with
          Razorpay and are marked <strong>unverified</strong>. The money was
          taken, but the confirmation did not come back. Check these against the
          Razorpay dashboard.
        </div>
      )}

      <div class="row mt-3 mb-2">
        <div class="col-md-3 mb-2">
          <select
            class="form-control"
            value={filters.type}
            onChange={(e) => set("type", e.target.value)}
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div class="col-md-2 mb-2">
          <select
            class="form-control"
            value={filters.mode}
            onChange={(e) => set("mode", e.target.value)}
          >
            <option value="all">Any mode</option>
            <option value="online">Gateway</option>
            <option value="offline">By hand</option>
          </select>
        </div>
        <div class="col-md-2 mb-2">
          <select
            class="form-control"
            value={filters.status}
            onChange={(e) => set("status", e.target.value)}
          >
            <option value="all">Any status</option>
            <option value="Completed">Completed</option>
            <option value="unverified">Unverified</option>
          </select>
        </div>
        <div class="col-md-2 mb-2">
          <input
            type="date"
            class="form-control"
            value={filters.from}
            onChange={(e) => set("from", e.target.value)}
          />
        </div>
        <div class="col-md-2 mb-2">
          <input
            type="date"
            class="form-control"
            value={filters.to}
            onChange={(e) => set("to", e.target.value)}
          />
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-md-6 mb-2">
          <input
            type="text"
            class="form-control"
            placeholder="Search name, roll number, center code or transaction id"
            value={filters.search}
            onChange={(e) => set("search", e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") load({ page: 1 });
            }}
          />
        </div>
        <div class="col-md-6 mb-2">
          <button
            class="btn btn-comman mr-2"
            type="button"
            onClick={() => load({ page: 1 })}
          >
            Search
          </button>
          <button
            class="btn btn-Next"
            type="button"
            onClick={download}
            disabled={downloading || !data || !data.payments.length}
          >
            {downloading ? "Preparing..." : "Download Excel"}
          </button>
        </div>
      </div>

      {data && (
        <Paging pagination={data.pagination} onPage={(p) => load({ page: p })} />
      )}

      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Date</th>
              <th>Type</th>
              <th>Paid By</th>
              <th>Reference</th>
              <th>Amount</th>
              <th>Mode</th>
              <th>Transaction ID</th>
              <th>Status</th>
              <th>Slip</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="10">Loading...</td>
              </tr>
            )}
            {!loading && data && data.payments.length === 0 && (
              <tr>
                <td colSpan="10">No payments match these filters.</td>
              </tr>
            )}
            {!loading &&
              data &&
              data.payments.map((p, i) => (
                <tr key={p.source + "-" + p.id}>
                  <td>{firstSr + i}</td>
                  <td>{dmy(p.paid_at)}</td>
                  <td>{p.type_label}</td>
                  <td>{p.paid_by}</td>
                  <td>{p.reference || "-"}</td>
                  <td>{rupees(p.amount)}</td>
                  <td>{p.channel_label || p.mode}</td>
                  <td>{p.trx_id || "-"}</td>
                  <td>
                    {p.status === "unverified" ? (
                      <span class="text-danger">unverified</span>
                    ) : (
                      p.status || "-"
                    )}
                  </td>
                  <td>
                    {p.slip_url ? (
                      <a
                        class="btn btn-sm btn-comman"
                        href={FILE_BASE + p.slip_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        PDF
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {data && (
        <Paging pagination={data.pagination} onPage={(p) => load({ page: p })} />
      )}
    </div>
  );
};

export default Payments;
